import { GradeLozenge } from "@idwal/idwal-react-components";
import { useTranslation } from "react-i18next";
import { SectionGradeSummary } from "../../utils/GradeUtils";
import "./GradingSummary.css";

export interface GradingSummaryProps {
    /** The three recalculated summary grades. Each value is null when there
     *  are no graded sections to compute it from, and renders blank/unset. */
    summary: SectionGradeSummary;
}

interface SummaryRowProps {
    label: string;
    value: number | null;
    testId: string;
}

const SummaryRow = ({ label, value, testId }: SummaryRowProps) => (
    <div className="flex align-items-center justify-content-between grading-summary-row" data-testid={testId}>
        <span className="grade-name font-semibold">{label}</span>
        {value === null ? <span className="grading-summary-unset">—</span> : <GradeLozenge grade={value} />}
    </div>
);

/**
 * The three read-only summary grades at the top of the Grading page, as
 * full-width rows (Idwal / Condition / Management). Values are computed by the
 * page (via `calculateSectionGrades`) so they update in real time as section
 * grades change, without a save. They use the same grade lozenge as the
 * read-only section grades. A null value renders blank rather than 0.
 */
export const GradingSummary = ({ summary }: GradingSummaryProps) => {
    const { t } = useTranslation("locale");

    return (
        <div className="grading-summary">
            <div className="flex align-items-center justify-content-between grading-summary-header">
                <span className="small-text">{t("grading.table.section")}</span>
                <span className="small-text grading-summary-grade-header">{t("grading.table.grade")}</span>
            </div>
            <SummaryRow testId="grading-summary-idwal" label={t("grading.summary.idwalGrade")} value={summary.overallGrade} />
            <SummaryRow testId="grading-summary-condition" label={t("grading.summary.conditionGrade")} value={summary.conditionGrade} />
            <SummaryRow testId="grading-summary-management" label={t("grading.summary.managementGrade")} value={summary.managementGrade} />
        </div>
    );
};
